import {
  Anchor,
  Badge,
  Code,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Table,
} from "@mantine/core";
import { List, ShowButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef, Header } from "@tanstack/react-table";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import { colorsByBrand } from "entities/plan/brand";
import { Subscription } from "entities/subscription";
import { colorsByStatus, selectStatuses } from "entities/subscription/status";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { getSubscriptionUrl } from "utils/chargebee";

export const SubscriptionList: React.FC = () => {
  const columns = useMemo<ColumnDef<Subscription>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
      },
      {
        id: "remote_id",
        header: "Remote ID",
        accessorKey: "remote_id",
        cell: function render({ getValue }) {
          const remoteId = getValue<Subscription["remote_id"]>();

          return (
            <Anchor href={getSubscriptionUrl(remoteId)}>{remoteId}</Anchor>
          );
        },
      },
      {
        id: "user",
        header: "User",
        accessorKey: "user",
        cell: function render({ getValue }) {
          const user = getValue<Subscription["user"]>();

          return (
            <Anchor component={Link} to={`/users/show/${user?.id}`}>
              {user?.email}
            </Anchor>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: function render({ getValue }) {
          const status = getValue<Subscription["status"]>();

          return <Badge color={colorsByStatus[status]}>{status}</Badge>;
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<Subscription, any>;
          }) => {
            return (
              <div>
                <Select
                  placeholder="Status"
                  data={selectStatuses}
                  onChange={header.column.setFilterValue}
                  clearable
                />
              </div>
            );
          },
        },
      },
      {
        id: "plan",
        header: "Plan",
        accessorKey: "plan",
        cell: function render({ getValue }) {
          const plan = getValue<Subscription["plan"]>();

          return (
            <Anchor component={Link} to={`/plans/show/${plan?.id}`}>
              <Badge color={colorsByBrand[plan?.brand]}>{plan?.name}</Badge>
            </Anchor>
          );
        },
      },
      {
        id: "next_billing_at",
        header: "Next Billing",
        accessorKey: "next_billing_at",
        cell: function render({ getValue }) {
          const nextBillingAt = getValue<Subscription["next_billing_at"]>();
          if (!nextBillingAt) return <div />;
          return <Code>{nextBillingAt}</Code>;
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "id",
        cell: function render({ getValue }) {
          return (
            <Group spacing="xs" noWrap>
              <ShowButton hideText recordItemId={getValue() as number} />
            </Group>
          );
        },
      },
    ],
    [],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
      pagination: {
        pageSize: 50,
      },
    },
  });

  return (
    <List>
      <AppliedFilters<Subscription> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<Subscription> headerGroups={getHeaderGroups()} />
          <Rows<Subscription> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <br />
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
